import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

// ---------------------------------------------------------------- row shapes

export interface SkillCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  updated_at: string;
}

export type SkillStatus = 'draft' | 'published' | 'archived';

export interface Skill {
  id: string;
  category_id: string | null;
  slug: string;
  name: string;
  description: string | null;
  status: SkillStatus;
  version: string;
  tags: string[];
  updated_at: string;
}

export interface SkillFile {
  id: string;
  skill_id: string;
  path: string;
  content: string;
  /** GENERATED ALWAYS AS octet_length(content) — nullable in the schema. */
  bytes: number | null;
  sort_order: number;
  updated_at: string;
}

export interface CategoryInput {
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
}

export interface SkillInput {
  category_id: string | null;
  slug: string;
  name: string;
  description: string | null;
  status: SkillStatus;
  version: string;
  tags: string[];
}

// ---------------------------------------------------------------------- keys

export const categoriesKey = (orgId: string | null) => ['skill-categories', orgId] as const;
export const skillsKey = (orgId: string | null) => ['skills', orgId] as const;
export const filesKey = (skillId: string | null) => ['skill-files', skillId] as const;

// ---------------------------------------------------------------- categories

export function useSkillCategories(orgId: string | null) {
  return useQuery({
    queryKey: categoriesKey(orgId),
    queryFn: async (): Promise<SkillCategory[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('skill_categories')
        .select('id, slug, name, description, icon, sort_order, updated_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useUpsertCategory(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: CategoryInput }) => {
      if (!orgId) throw new Error('אין ארגון פעיל');
      if (id) {
        const { error } = await supabase.from('skill_categories').update(values).eq('id', id);
        if (error) throw error;
        return;
      }
      const { error } = await supabase
        .from('skill_categories')
        .insert({ ...values, organization_id: orgId });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: categoriesKey(orgId) });
    },
  });
}

export function useDeleteCategory(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    // Soft delete. Skills keep their rows and fall back to "ללא נושא" because
    // the FK is ON DELETE SET NULL; losing the grouping must never lose a skill.
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('skill_categories')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: categoriesKey(orgId) });
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}

// -------------------------------------------------------------------- skills

export function useSkills(orgId: string | null) {
  return useQuery({
    queryKey: skillsKey(orgId),
    queryFn: async (): Promise<Skill[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('skills')
        .select('id, category_id, slug, name, description, status, version, tags, updated_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('name', { ascending: true })
        // status is a CHECK constraint the type generator emits as `string`.
        .overrideTypes<{ status: SkillStatus }[]>();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useUpsertSkill(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: SkillInput }) => {
      if (!orgId) throw new Error('אין ארגון פעיל');
      if (id) {
        const { error } = await supabase.from('skills').update(values).eq('id', id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase
        .from('skills')
        .insert({ ...values, organization_id: orgId })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}

export function useDeleteSkill(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('skills')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}

// --------------------------------------------------------------------- files

export function useSkillFiles(skillId: string | null) {
  return useQuery({
    queryKey: filesKey(skillId),
    queryFn: async (): Promise<SkillFile[]> => {
      if (!skillId) return [];
      const { data, error } = await supabase
        .from('skill_files')
        .select('id, skill_id, path, content, bytes, sort_order, updated_at')
        .eq('skill_id', skillId)
        .order('sort_order', { ascending: true })
        .order('path', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: Boolean(skillId),
    staleTime: 30 * 1000,
  });
}

export function useSaveFile(orgId: string | null, skillId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, path, content }: { id: string | null; path: string; content: string }) => {
      if (!orgId || !skillId) throw new Error('אין הקשר לסקיל');
      const clean = path.trim().replace(/^\/+/, '');
      if (!clean) throw new Error('נתיב הקובץ ריק');
      if (clean.includes('..') || clean.includes('\\')) throw new Error('נתיב לא חוקי');
      if (id) {
        const { error } = await supabase.from('skill_files').update({ path: clean, content }).eq('id', id);
        if (error) throw error;
        return;
      }
      const { error } = await supabase
        .from('skill_files')
        .insert({ organization_id: orgId, skill_id: skillId, path: clean, content });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: filesKey(skillId) });
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}

export function useDeleteFile(orgId: string | null, skillId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    // Files are hard-deleted. The publish snapshot in skill_versions is the
    // recovery path, which is why publishing before a big edit matters.
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('skill_files').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: filesKey(skillId) });
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}

// ------------------------------------------------------------------ publish

export function usePublishSkill(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      skillId,
      version,
      note,
      files,
    }: {
      skillId: string;
      version: string;
      note: string | null;
      files: SkillFile[];
    }) => {
      if (!orgId) throw new Error('אין ארגון פעיל');
      const snapshot = files.map((f) => ({ path: f.path, content: f.content }));
      const { error: versionError } = await supabase.from('skill_versions').insert({
        organization_id: orgId,
        skill_id: skillId,
        version,
        note,
        snapshot,
        created_by: userId,
      });
      if (versionError) throw versionError;
      const { error } = await supabase
        .from('skills')
        .update({ status: 'published', version })
        .eq('id', skillId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}
